import React, { createContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { useAuth } from 'react-oidc-context';
import { getEnv } from '../utils/environment';
import defaultPermissions from './permissions.default.json';

export const Roles = createContext({});

const env = getEnv();
const subPath = env === 'prod' ? '' : env;
const apiUrl = `https://sfe.dsmotd${subPath}.toyota.com/otdgetadminpermission/get-admin-permissions`; // permissions api url

// Local role decoding only controls UI visibility; APIs must still enforce authorization.
const useLocalRolePermissions =
  ['localhost', '127.0.0.1'].includes(window.location.hostname) &&
  process.env.REACT_APP_LOCAL_ROLE_PERMISSIONS !== 'false';

const setAllPermissions = (permissions) => {
  Object.keys(permissions).forEach((key) => {
    if (typeof permissions[key] === 'object' && permissions[key] !== null) {
      setAllPermissions(permissions[key]);
    } else {
      permissions[key] = true;
    }
  });
};

export const permissionsFromRoles = (roles) => {
  const permissions = JSON.parse(JSON.stringify(defaultPermissions));
  const normalizedRoles = Array.isArray(roles)
    ? roles.filter((role) => typeof role === 'string').map((role) => role.toUpperCase())
    : [];

  if (normalizedRoles.some((role) => ['SFE_ADMIN', 'CUBE_SFE_ADMIN'].includes(role))) {
    setAllPermissions(permissions);
    return permissions;
  }

  if (
    normalizedRoles.some((role) => ['SFE_READ_ONLY', 'CUBE_SFE_GENERAL_INQUIRY'].includes(role))
  ) {
    permissions.summary.view = true;
    permissions.search.view = true;
    permissions.edit.view = true;
    permissions.reports.tableau.analytics = true;
  }

  if (normalizedRoles.some((role) => ['SFE_REPORTS', 'CUBE_SFE_REPORTS'].includes(role))) {
    permissions.reports.tableau.analytics = true;
    permissions.reports.tableau.simulation = true;
  }

  return permissions;
};

const permissionsFromAccessToken = (token) => {
  try {
    return permissionsFromRoles(jwtDecode(token).roles);
  } catch {
    return defaultPermissions;
  }
};

const RolesProvider = ({ children }) => {
  const auth = useAuth();

  const [permissions, setPermissions] = useState(null);

  useEffect(() => {
    const token = auth?.user?.access_token;
    if (!token) {
      return;
    }

    if (useLocalRolePermissions) {
      setPermissions(permissionsFromAccessToken(token));
      return;
    }

    const body = { access_token: token };
    const headers = { Authorization: token };

    axios
      .post(apiUrl, body, { headers })
      .then((response) => {
        setPermissions(response?.data || defaultPermissions);
      })
      .catch((e) => e);
  }, [auth?.user?.access_token]);

  return <Roles.Provider value={{ permissions }}>{children}</Roles.Provider>;
};

RolesProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default RolesProvider;
